import { Component, Input, OnInit, OnChanges, Output, EventEmitter, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SMA, SMADto } from 'src/app/models/sma';
import { DialogAddSmaComponent } from '../dialog-add-sma/dialog-add-sma.component';
import { RestService } from 'src/app/services/rest.service';
import { ShareClientId } from 'src/app/services/data/companies.service';

@Component({
  selector: 'app-mat-table-sma',
  templateUrl: './mat-table-sma.component.html',
  styleUrl: './mat-table-sma.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class MatTableSmaComponent implements OnInit, OnChanges {
  @Input() licenceGuid: string = '';
  @Input() licenceId: number = 0;
  @Input() dataSource: SMA[] = [];
  @Input() unsavedLicence: boolean = false; // Flag to indicate licence is not yet saved

  @Output() smaAdded = new EventEmitter<SMADto>();

  constructor(
    private dialog: MatDialog,
    private restService: RestService,
    public shareClientId: ShareClientId,
    private cdr: ChangeDetectorRef
  ) {}

  trackBySma(index: number, item: SMA): any {
    return item.guid || item.start_date + item.end_date || index;
  }

  ngOnInit(): void {
    console.log('MatTableSmaComponent ngOnInit:', {
      unsavedLicence: this.unsavedLicence,
      licenceGuid: this.licenceGuid,
      licenceId: this.licenceId,
      tmpSmaList: this.shareClientId.tmpSmaList,
      dataSource: this.dataSource
    });

    // If it's a new unsaved licence, use tmpSmaList or provided dataSource
    if (this.unsavedLicence) {
      // If dataSource was provided from parent (e.g., loaded from backend), use it
      if (this.dataSource && this.dataSource.length > 0) {
        console.log('Using provided dataSource:', this.dataSource);
        this.shareClientId.tmpSmaList = [...this.dataSource];
      } else {
        // Otherwise use tmpSmaList from ShareClientId
        this.dataSource = [...this.shareClientId.tmpSmaList];
        console.log('Using tmpSmaList for unsaved licence:', this.dataSource);
      }
      this.sortData();
      // Force change detection
      this.cdr.detectChanges();
    } else if (this.licenceGuid) {
      // Load existing SMA data if licenceGuid is provided
      this.loadSmaData();
    }
  }

  ngOnChanges(): void {
    console.log('MatTableSmaComponent ngOnChanges:', {
      unsavedLicence: this.unsavedLicence,
      licenceGuid: this.licenceGuid,
      licenceId: this.licenceId,
      dataSource: this.dataSource
    });

    // If dataSource is provided from parent and has changed, update tmpSmaList
    if (this.unsavedLicence && this.dataSource && this.dataSource.length > 0) {
      console.log('DataSource changed, updating tmpSmaList:', this.dataSource);
      this.shareClientId.tmpSmaList = [...this.dataSource];
      // Create new array reference to trigger change detection
      this.dataSource = [...this.dataSource];
    }

    this.sortData();

    // Reload data when licenceGuid changes and is valid (only for saved licences)
    if (this.licenceGuid && !this.unsavedLicence) {
      this.loadSmaData();
    }
  }

  private loadSmaData(): void {
    // Don't make API call if licenceGuid is invalid
    if (!this.licenceGuid) {
      console.log('Skipping SMA data load - invalid licenceGuid:', this.licenceGuid);
      return;
    }

    console.log('Loading SMA data for licenceGuid:', this.licenceGuid);
    this.restService.get<any>(`/licences/period?licenceId=${this.licenceGuid}`).subscribe(
      (response) => {
        console.log('SMA data response (raw):', response);
        let rawData: any[] = [];

        // Backend returns array directly, not wrapped in success/data
        if (Array.isArray(response)) {
          rawData = response;
          console.log('DataSource is direct array');
        } else if (response && response.data) {
          // Fallback: check if wrapped in data property
          rawData = Array.isArray(response.data) ? response.data : [response.data];
          console.log('DataSource from data property');
        }

        // Transform backend format (PascalCase) to frontend format (snake_case)
        this.dataSource = rawData.map((item: any) => {
          const transformed = {
            id: item.ID || item.id,
            guid: item.GUID || item.guid,
            licence_id: item.LicenceID || item.licence_id,
            document_number: item.DocumentNumber || item.document_number || '',
            product_id: item.ProductID || item.product_id || '',
            start_date: item.StartDate || item.start_date,
            end_date: item.EndDate || item.end_date,
            sma_booked: item.SMABooked || item.sma_booked || false,
            created_at: item.CreatedAt || item.created_at,
            updated_at: item.UpdatedAt || item.updated_at,
            project_number: item.ProjectNumber || item.project_number || '',
            company_name: item.CompanyName || item.company_name || '',
            project_name: item.ProjectName || item.project_name || '',
            customer_order_number: item.CustomerOrderNumber || item.customer_order_number || ''
          };
          console.log('Transformed item:', transformed);
          return transformed;
        });

        console.log('DataSource updated (transformed):', this.dataSource);
        this.sortData();

        // Force change detection
        this.cdr.detectChanges();
      },
      (error) => {
        console.log('No SMA data found for licence or error loading:', error);
        // Initialize empty dataSource on error
        this.dataSource = [];
      }
    );
  }

  private sortData(): void {
    // Sortierung nach Enddatum (absteigend) und dann nach Product ID (aufsteigend)
    if (!this.dataSource || this.dataSource.length === 0) {
      return;
    }

    this.dataSource.sort((a, b) => {
      const dateCompare = new Date(b.end_date).getTime() - new Date(a.end_date).getTime();
      if (dateCompare !== 0) {
        return dateCompare;
      }
      // Only sort by product_id if it exists
      if (a.product_id && b.product_id) {
        return a.product_id.localeCompare(b.product_id);
      }
      return 0;
    });
  }

  addSma(): void {
    console.log('=== ADD SMA CLICKED ===');
    console.log('Current state:', {
      unsavedLicence: this.unsavedLicence,
      dataSourceLength: this.dataSource.length,
      tmpSmaListLength: this.shareClientId.tmpSmaList.length,
      dataSource: this.dataSource,
      tmpSmaList: this.shareClientId.tmpSmaList
    });

    const dialogRef = this.dialog.open(DialogAddSmaComponent, {
      width: '600px',
      data: {
        licenceGuid: this.licenceGuid,
        licenceId: this.licenceId,
        saveLicenceFirst: this.unsavedLicence
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('=== DIALOG CLOSED ===');
      console.log('Dialog result:', result);

      if (result && result.sma) {
        console.log('Raw SMA data from dialog:', result.sma);

        if (this.unsavedLicence) {
          // For new licences, add to tmpSmaList (will be saved with licence)
          // Transform the SMA data to match the expected format
          const transformedSma = {
            id: result.sma.ID || result.sma.id,
            guid: result.sma.GUID || result.sma.guid,
            licence_id: result.sma.LicenceID || result.sma.licence_id,
            document_number: result.sma.DocumentNumber || result.sma.document_number || '',
            product_id: result.sma.ProductID || result.sma.product_id || '',
            start_date: result.sma.StartDate || result.sma.start_date,
            end_date: result.sma.EndDate || result.sma.end_date,
            sma_booked: result.sma.SMABooked || result.sma.sma_booked || false,
            created_at: result.sma.CreatedAt || result.sma.created_at,
            updated_at: result.sma.UpdatedAt || result.sma.updated_at,
            project_number: result.sma.ProjectNumber || result.sma.project_number || '',
            company_name: result.sma.CompanyName || result.sma.company_name || '',
            project_name: result.sma.ProjectName || result.sma.project_name || '',
            customer_order_number: result.sma.CustomerOrderNumber || result.sma.customer_order_number || ''
          };

          console.log('Transformed SMA:', transformedSma);

          this.shareClientId.tmpSmaList.push(transformedSma);
          console.log('tmpSmaList after push:', this.shareClientId.tmpSmaList);

          this.dataSource = [...this.shareClientId.tmpSmaList];
          console.log('dataSource after update:', this.dataSource);

          this.sortData();
          this.cdr.detectChanges();

          console.log('Final state after add:', {
            dataSourceLength: this.dataSource.length,
            tmpSmaListLength: this.shareClientId.tmpSmaList.length
          });
        } else {
          // For existing licences, reload data from server
          console.log('Reloading data from server for saved licence');
          this.loadSmaData();
        }
        this.smaAdded.emit(result.sma);
      } else {
        console.log('No SMA data in result or dialog was cancelled');
      }
    });
  }

  editSma(sma: SMA): void {
    const dialogRef = this.dialog.open(DialogAddSmaComponent, {
      width: '600px',
      data: {
        licenceGuid: this.licenceGuid,
        licenceId: this.licenceId,
        smaGuid: sma.guid,
        saveLicenceFirst: this.unsavedLicence
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.sma) {
        console.log('SMA updated');

        if (this.unsavedLicence) {
          // For unsaved licences, update tmpSmaList
          const index = this.shareClientId.tmpSmaList.findIndex(s => s.guid === sma.guid);
          if (index > -1) {
            // Transform the updated SMA data
            const transformedSma = {
              id: result.sma.ID || result.sma.id,
              guid: result.sma.GUID || result.sma.guid || sma.guid,
              licence_id: result.sma.LicenceID || result.sma.licence_id,
              document_number: result.sma.DocumentNumber || result.sma.document_number || '',
              product_id: result.sma.ProductID || result.sma.product_id || '',
              start_date: result.sma.StartDate || result.sma.start_date,
              end_date: result.sma.EndDate || result.sma.end_date,
              sma_booked: result.sma.SMABooked || result.sma.sma_booked || false,
              created_at: result.sma.CreatedAt || result.sma.created_at,
              updated_at: result.sma.UpdatedAt || result.sma.updated_at,
              project_number: result.sma.ProjectNumber || result.sma.project_number || '',
              company_name: result.sma.CompanyName || result.sma.company_name || '',
              project_name: result.sma.ProjectName || result.sma.project_name || '',
              customer_order_number: result.sma.CustomerOrderNumber || result.sma.customer_order_number || ''
            };

            this.shareClientId.tmpSmaList[index] = transformedSma;
            this.dataSource = [...this.shareClientId.tmpSmaList];
            this.sortData();
            this.cdr.detectChanges();
            console.log('SMA updated in tmpSmaList:', this.shareClientId.tmpSmaList);
          }
        } else {
          // For saved licences, reload from server
          this.loadSmaData();
        }

        this.smaAdded.emit(result.sma);
      }
    });
  }

  deleteSma(sma: SMA): void {
    if (confirm('Are you sure you want to delete this SMA entry?')) {
      if (this.unsavedLicence) {
        // For unsaved licences, just remove from tmpSmaList
        const index = this.shareClientId.tmpSmaList.findIndex(s =>
          (s.guid && s.guid === sma.guid) ||
          (s.start_date === sma.start_date && s.end_date === sma.end_date)
        );
        if (index > -1) {
          this.shareClientId.tmpSmaList.splice(index, 1);
          this.dataSource = [...this.shareClientId.tmpSmaList];
          this.sortData();
          this.cdr.detectChanges();
          console.log('SMA removed from tmpSmaList:', this.shareClientId.tmpSmaList);
        }
      } else if (sma.guid) {
        // For saved licences, delete from server
        this.restService.delete<any>(`/licences/period/${sma.guid}`).subscribe(
          (response) => {
            console.log('SMA deleted successfully');
            // Remove from dataSource
            const index = this.dataSource.indexOf(sma);
            if (index > -1) {
              this.dataSource.splice(index, 1);
              // Create new array reference for change detection
              this.dataSource = [...this.dataSource];
              this.sortData();
              this.cdr.detectChanges();
            }
          },
          (error) => {
            console.error('Error deleting SMA:', error);
          }
        );
      }
    }
  }
}
